import React, { useState, useEffect, createContext, useContext } from 'react';
import { CheckOne, CloseOne, Caution, Info } from '@icon-park/react';
import './Toast.css';

// Toast Context
const ToastContext = createContext(null);

/**
 * Toast Provider 组件
 * 包裹在应用最外层，提供全局 Toast 功能
 */
export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const addToast = (message, type = 'info', duration = 3000) => {
    const id = Date.now() + Math.random();
    const newToast = { id, message, type, duration };
    
    setToasts(prev => [...prev, newToast]);

    // 自动移除
    if (duration > 0) {
      setTimeout(() => {
        removeToast(id);
      }, duration);
    }

    return id;
  };

  const removeToast = (id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  const success = (message, duration) => addToast(message, 'success', duration);
  const error = (message, duration) => addToast(message, 'error', duration);
  const warning = (message, duration) => addToast(message, 'warning', duration);
  const info = (message, duration) => addToast(message, 'info', duration);

  return (
    <ToastContext.Provider value={{ success, error, warning, info, addToast, removeToast }}>
      {children}
      <ToastContainer toasts={toasts} onClose={removeToast} />
    </ToastContext.Provider>
  );
};

/**
 * Toast Container 组件
 * 渲染所有 Toast 消息
 */
const ToastContainer = ({ toasts, onClose }) => {
  return (
    <div className="toast-container">
      {toasts.map(toast => (
        <Toast
          key={toast.id}
          {...toast}
          onClose={() => onClose(toast.id)}
        />
      ))}
    </div>
  );
};

/**
 * Toast 组件
 * 单个 Toast 消息
 */
const Toast = ({ id, message, type, onClose }) => {
  const [isLeaving, setIsLeaving] = useState(false);

  const handleClose = () => {
    setIsLeaving(true);
    setTimeout(() => {
      onClose(id);
    }, 300); // 动画时长
  };

  // 图标映射
  const getIcon = () => {
    switch (type) {
      case 'success':
        return <CheckOne theme="filled" size={18} fill="#52c41a" />;
      case 'error':
        return <CloseOne theme="filled" size={18} fill="#ff4d4f" />;
      case 'warning':
        return <Caution theme="filled" size={18} fill="#faad14" />;
      case 'info':
      default:
        return <Info theme="filled" size={18} fill="#1890ff" />;
    }
  };

  return (
    <div className={`toast toast-${type} ${isLeaving ? 'toast-leaving' : ''}`}>
      <div className="toast-icon">{getIcon()}</div>
      <div className="toast-message">{message}</div>
      <button className="toast-close" onClick={handleClose}><CloseOne theme="outline" size={14} fill="currentColor" /></button>
    </div>
  );
};

/**
 * useToast Hook
 * 在组件中使用 Toast 功能
 */
export const useToast = () => {
  const context = useContext(ToastContext);
  
  if (!context) {
    throw new Error('useToast must be used within ToastProvider');
  }
  
  return context;
};

export default Toast;

